/**
 * BLE scan service — thin wrapper over the Capacitor BLE plugin's scan API.
 *
 * Manages scan lifecycle (start, callback dispatch) for the on-site Bluetooth
 * provisioning flow. Each scan de-duplicates results per deviceId so callers
 * receive at most one result per physical device. Callers are responsible for
 * stopping the scan via stopBleScan() (or the context's stopScan()).
 *
 * Contains:
 * - startBleScan — begin a scan and stream ScanResult objects to a callback
 * - stopBleScan  — stop any active scan
 */

import { BleClient } from '@capacitor-community/bluetooth-le';

/**
 * Start a BLE scan.
 *
 * Invokes `onDeviceFound` at most once per deviceId per scan session.
 * The scan runs until `stopBleScan()` is called.
 *
 * @param {function(import('@capacitor-community/bluetooth-le').ScanResult): void} onDeviceFound
 *   Called with the raw ScanResult when a new device is first seen.
 * @param {object} [options]
 * @param {string[]} [options.services=[]]          Filter by GATT service UUIDs.
 * @param {boolean}  [options.allowDuplicates=false] Pass duplicate advertisements to the callback.
 * @returns {Promise<void>}
 */
export async function startBleScan(onDeviceFound, options = {}) {
  const { services = [], allowDuplicates = false } = options;

  const seenDevices = new Set();

  await BleClient.requestLEScan(
    {
      ...(services.length > 0 && { services }),
      allowDuplicates,
      scanMode: 2, // SCAN_MODE_LOW_LATENCY — required for manufacturer data (ICCID)
    },
    result => {
      const deviceId = result.device?.deviceId;

      if (!seenDevices.has(deviceId)) {
        seenDevices.add(deviceId);
        onDeviceFound(result);
      }
    },
  );
}

/**
 * Stop the current BLE scan.
 *
 * Safe to call even when no scan is active; the plugin handles the no-op.
 *
 * @returns {Promise<void>}
 */
export async function stopBleScan() {
  await BleClient.stopLEScan();
}
